// A simple parser to convert RRULE strings into human-readable text.

export const formatRecurrence = (recurrence: string[]): string | null => {
  if (!recurrence || recurrence.length === 0) {
    return null;
  }

  // For now, just focus on the first RRULE string.
  const rrule = recurrence.find((r) => r.startsWith("RRULE:"));
  if (!rrule) {
    // Handle other recurrence types like RDATE if necessary in the future
    return "Custom schedule";
  }

  const parts = rrule.replace("RRULE:", "").split(";");
  const rules: { [key: string]: string } = {};
  parts.forEach((part) => {
    const [key, value] = part.split("=");
    if (key && value) {
      rules[key] = value;
    }
  });

  let description = "";

  const freqMap: { [key: string]: string } = {
    DAILY: "Day",
    WEEKLY: "Week",
    MONTHLY: "Month",
    YEARLY: "Year",
  };

  if (rules.INTERVAL && parseInt(rules.INTERVAL) > 1) {
    description = `Every ${rules.INTERVAL} ${freqMap[rules.FREQ]?.toLowerCase() ?? ""}s`;
  } else {
    description = rules.FREQ?.charAt(0) + rules.FREQ?.slice(1).toLowerCase() || "Event";
  }

  if (rules.BYDAY) {
    const days = rules.BYDAY.split(",").map(dayToFullName).join(", ");
    description += ` on ${days}`;
  }

  if (rules.COUNT) {
    description += `, for ${rules.COUNT} occurrences`;
  }

  if (rules.UNTIL) {
    try {
      const untilDate = parseUntilDate(rules.UNTIL);
      description += ` until ${untilDate.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}`;
    } catch (e) {
      console.error("Could not parse UNTIL date:", e);
    }
  }

  return description;
};

const dayToFullName = (day: string): string => {
  const days: { [key: string]: string } = {
    SU: "Sunday",
    MO: "Monday",
    TU: "Tuesday",
    WE: "Wednesday",
    TH: "Thursday",
    FR: "Friday",
    SA: "Saturday",
  };
  // The rule might contain a number like "2MO" for "second Monday".
  // We can ignore the number for this simple description.
  return days[day.slice(-2)] || day;
};

const parseUntilDate = (until: string): Date => {
  // Format: YYYYMMDDTHHMMSSZ
  const year = parseInt(until.substring(0, 4));
  const month = parseInt(until.substring(4, 6)) - 1; // JS months are 0-indexed
  const day = parseInt(until.substring(6, 8));
  const hour = parseInt(until.substring(9, 11)) || 0;
  const minute = parseInt(until.substring(11, 13)) || 0;
  const second = parseInt(until.substring(13, 15)) || 0;
  return new Date(Date.UTC(year, month, day, hour, minute, second));
};
